import { useState, useRef, useEffect } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useNavigate } from "react-router-dom"
import {
  ChevronDown, ChevronUp, Search, Video,
  Clock, Users, Calendar, Filter, BarChart2, Radio
} from "lucide-react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/clientWeb/components/ui/table"

interface Webinar {
  id: string
  title: string
  host: string
  attendees: number
  registrants: number
  date: string
  duration: string
  status: "completed" | "cancelled" | "upcoming"
}

const mockWebinars: Webinar[] = [
  { id: "w1", title: "AI in Modern Healthcare",       host: "Sarah Wilson",  attendees: 142, registrants: 180, date: "Apr 15, 2024", duration: "72 min",  status: "completed" },
  { id: "w2", title: "DevOps Best Practices",          host: "Mike Chen",     attendees: 98,  registrants: 120, date: "Apr 13, 2024", duration: "58 min",  status: "completed" },
  { id: "w3", title: "UX Design Fundamentals",         host: "Emma Davis",    attendees: 190, registrants: 230, date: "Apr 11, 2024", duration: "65 min",  status: "completed" },
  { id: "w4", title: "Cloud Architecture 2025",        host: "Alex Johnson",  attendees: 150, registrants: 180, date: "Apr 10, 2024", duration: "47 min",  status: "completed" },
  { id: "w5", title: "React Performance Patterns",     host: "Sarah Wilson",  attendees: 85,  registrants: 110, date: "Apr 8, 2024",  duration: "55 min",  status: "completed" },
  { id: "w6", title: "Q4 Planning Session",            host: "David Kim",     attendees: 0,   registrants: 45,  date: "Apr 12, 2024", duration: "–",       status: "cancelled" },
  { id: "w7", title: "Machine Learning 101",           host: "Emma Davis",    attendees: 320, registrants: 400, date: "Apr 5, 2024",  duration: "90 min",  status: "completed" },
  { id: "w8", title: "Product Launch Preview",         host: "Mike Chen",     attendees: 0,   registrants: 200, date: "Apr 14, 2024", duration: "–",       status: "cancelled" },
  { id: "w9", title: "Kubernetes Deep Dive",           host: "Alex Johnson",  attendees: 210, registrants: 260, date: "Apr 3, 2024",  duration: "85 min",  status: "completed" },
  { id: "w10", title: "Agile Leadership Masterclass",  host: "David Kim",     attendees: 175, registrants: 210, date: "Apr 1, 2024",  duration: "70 min",  status: "completed" },
]

type SortKey = "title" | "host" | "attendees" | "registrants" | "date" | "duration"
type SortDir = "asc" | "desc"

function FilterDropdown({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const t = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener("mousedown", h)
    return () => document.removeEventListener("mousedown", h)
  }, [])
  const filters = [t("webinar.today"), t("webinar.yesterday"), t("webinar.lastWeek"), t("webinar.lastMonth")]
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-2 border border-stroke-100 dark:border-border dark:border-border bg-card dark:bg-card rounded-lg px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:border-primary transition-colors">
        <Filter className="w-3.5 h-3.5" /> {value} <ChevronDown className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-20 bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-xl shadow-lg min-w-[140px] py-1">
          {filters.map(f => (
            <button key={f} onClick={() => { onChange(f); setOpen(false) }}
              className={`block w-full text-left px-4 py-2 text-sm transition-colors ${value === f ? "text-primary font-semibold" : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"}`}>
              {f}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function SortIcon({ col, sortKey, sortDir }: { col: SortKey; sortKey: SortKey; sortDir: SortDir }) {
  if (col !== sortKey) return <ChevronUp className="w-3 h-3 opacity-20" />
  return sortDir === "asc" ? <ChevronUp className="w-3 h-3 text-primary" /> : <ChevronDown className="w-3 h-3 text-primary" />
}

const STATUS_STYLE: Record<Webinar["status"], string> = {
  completed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400",
  cancelled:  "bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400",
  upcoming:   "bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400",
}

export default function TotalWebinarsPage() {
  const t = useTranslation()
  const navigate = useNavigate()
  const [filter, setFilter] = useState("Last Week")
  const [search, setSearch] = useState("")
  const [sortKey, setSortKey] = useState<SortKey>("date")
  const [sortDir, setSortDir] = useState<SortDir>("desc")

  const filtered = mockWebinars
    .filter(w =>
      w.title.toLowerCase().includes(search.toLowerCase()) ||
      w.host.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      let av: string | number = a[sortKey]
      let bv: string | number = b[sortKey]
      if (sortKey === "attendees" || sortKey === "registrants") {
        av = Number(av); bv = Number(bv)
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1
      if (av > bv) return sortDir === "asc" ? 1 : -1
      return 0
    })

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(d => d === "asc" ? "desc" : "asc")
    else { setSortKey(key); setSortDir("asc") }
  }

  const cols: { key: SortKey; label: string }[] = [
    { key: "title",       label: t("webinar.title") },
    { key: "host",        label: t("webinar.host") },
    { key: "registrants", label: t("webinar.registrants") },
    { key: "attendees",   label: t("webinar.attendees") },
    { key: "date",        label: t("webinar.date") },
    { key: "duration",    label: t("webinar.duration") },
  ]

  return (
    <div className="flex flex-col h-full w-full overflow-y-auto bg-background p-4 md:p-6 gap-5">

      {/* Breadcrumb + Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
          <button onClick={() => navigate("/webinar/analytics")} className="hover:text-primary text-primary font-semibold transition-colors flex items-center gap-1">
            <BarChart2 className="w-3.5 h-3.5" /> {t("webinar.webinarAnalytics")}
          </button>
          <span>/</span>
          <span className="text-foreground font-medium">{t("webinar.totalWebinars")}</span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-foreground">{t("webinar.totalWebinars")}</h1>
            <p className="text-xs text-slate-400 mt-0.5">{filtered.length} {t("webinar.webinarsFound")} · {t("webinar.clickRowViewAnalytics")}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder={t("webinar.searchWebinars")}
                className="pl-8 pr-3 py-2 text-sm bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-slate-900 dark:text-foreground/80 dark:text-white w-[200px]"
              />
            </div>
            <FilterDropdown value={filter} onChange={setFilter} />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card dark:bg-card rounded-2xl border border-stroke-100 dark:border-border dark:border-border shadow-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Video className="w-16 h-16 text-slate-200 dark:text-slate-700" />
            <p className="text-sm font-semibold text-slate-600 dark:text-slate-400">{t("webinar.noWebinarsFound")}</p>
            <p className="text-xs text-slate-400">{t("webinar.tryAdjustingSearchFilter")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-stroke-100 dark:border-border dark:border-border bg-slate-50 dark:bg-card/80">
                  {cols.map(c => (
                    <TableHead key={c.key}
                      className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-primary transition-colors select-none"
                      onClick={() => handleSort(c.key)}>
                      <span className="flex items-center gap-1">{c.label} <SortIcon col={c.key} sortKey={sortKey} sortDir={sortDir} /></span>
                    </TableHead>
                  ))}
                  <TableHead className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t("webinar.status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(w => (
                  <TableRow key={w.id}
                    className="border-b border-slate-50 dark:border-border/40 hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors cursor-pointer"
                    onClick={() => navigate(`/webinar/${w.id}/analytics`)}>
                    <TableCell className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Radio className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <span className="text-sm font-semibold text-primary hover:underline">{w.title}</span>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{w.host}</TableCell>
                    <TableCell className="px-4 py-3">
                      <span className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                        <Users className="w-3.5 h-3.5" /> {w.registrants}
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <span className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                        <Users className="w-3.5 h-3.5 text-emerald-500" /> {w.attendees}
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <span className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                        <Calendar className="w-3.5 h-3.5" /> {w.date}
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <span className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                        <Clock className="w-3.5 h-3.5" /> {w.duration}
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLE[w.status]}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${w.status === "completed" ? "bg-emerald-500" : w.status === "upcoming" ? "bg-blue-500" : "bg-red-500"}`} />
                        {w.status.charAt(0).toUpperCase() + w.status.slice(1)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  )
}
